import AddIcon from "@mui/icons-material/Add";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { CompetenceListField } from "core/field/FieldService";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AddEvent, Event } from "utils/history/Event";
import CompetenceListFieldLevelTable from "./CompetenceListFieldLevelTable";
import FieldBaseRow, { FieldRowProps } from "./FieldBaseRow";

export type CompetenceListFieldRowProps = FieldRowProps & {
	field: CompetenceListField;
};

const CompetenceListFieldRow: React.FC<CompetenceListFieldRowProps> = (
	props: CompetenceListFieldRowProps
) => {
	const LEVELS_FIELD = "/levels";
	const LEVELS_PATH = LEVELS_FIELD + "/";

	const { t } = useTranslation();

	const [newLevel, setNewlevel] = useState<number | undefined>(1);
	const [newLevelError, setNewLevelError] = useState(false);

	useEffect(() => {
		if (props.field.levels.length > 0) {
			setNewlevel(
				props.field.levels.reduce((a, b) => (a > b ? a : b)).level + 1
			);
		}
	}, [props.field.levels.length]);

	function handleChange(event: Event, lvl: number): void {
		const index = props.field.levels.findIndex(
			level => level.level === lvl
		);
		if (index === -1) {
			console.error("Could not find level " + lvl);
			return;
		}
		event.applyContext(LEVELS_PATH + index);
		props.onChange(event);
	}

	function updateNewLevel(event: React.ChangeEvent<HTMLInputElement>) {
		if (event.target.value === "") {
			setNewLevelError(true);
			setNewlevel(undefined);
		} else {
			const level = parseInt(event.target.value);
			const levelInvalid =
				level <= 0 ||
				props.field.levels.some(lvl => lvl.level === level);
			setNewLevelError(levelInvalid);
			setNewlevel(level);
		}
	}

	function insertNewLevel() {
		const path = LEVELS_PATH + props.field.levels.length;
		const value = {
			level: newLevel,
			elements: []
		};
		props.onChange(new AddEvent(path, value));
	}

	return (
		<FieldBaseRow {...props}>
			{[...props.field.levels]
				.sort((a, b) => a.level - b.level)
				.map(level => (
					<CompetenceListFieldLevelTable
						key={level.level}
						level={level}
						editable={props.editable}
						onChange={event => handleChange(event, level.level)}
					/>
				))}
			{props.editable && (
				<Grid container alignItems={"center"} spacing={1}>
					<Grid item>
						<Typography variant="body2">{t("level")}</Typography>
					</Grid>
					<Grid item>
						<TextField
							sx={{ width: "5ch" }}
							type="number"
							size="small"
							variant="standard"
							inputProps={{ min: "1" }}
							value={newLevel}
							error={newLevelError}
							onChange={updateNewLevel}
						/>
					</Grid>
					<Grid item>
						<IconButton
							disabled={newLevelError}
							onClick={insertNewLevel}>
							<AddIcon />
						</IconButton>
					</Grid>
				</Grid>
			)}
		</FieldBaseRow>
	);
};

export default CompetenceListFieldRow;
